/**
 * StatusPill — dot + label, color-coded by document status (design §2).
 * Resolves colors from the active theme.
 */
import React from 'react';
import { Text, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { DocStatus } from '../schema/billing/common';

type Tone = 'draft' | 'info' | 'warn' | 'success' | 'danger' | 'muted';

const STATUS_TONE: Record<string, { tone: Tone; label: string }> = {
  DRAFT: { tone: 'draft', label: 'Draft' },
  ISSUED: { tone: 'info', label: 'Issued' },
  PARTIALLY_PAID: { tone: 'warn', label: 'Part-paid' },
  PAID: { tone: 'success', label: 'Paid' },
  OVERDUE: { tone: 'danger', label: 'Overdue' },
  VOID: { tone: 'muted', label: 'Void' },
  WRITTEN_OFF: { tone: 'muted', label: 'Written off' },
  ACCEPTED: { tone: 'success', label: 'Accepted' },
  DECLINED: { tone: 'danger', label: 'Declined' },
  EXPIRED: { tone: 'muted', label: 'Expired' },
};

export const statusLabel = (s: string) => STATUS_TONE[s]?.label ?? s;

const StatusPill: React.FC<{ status: DocStatus | string }> = ({ status }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const def = STATUS_TONE[status] ?? STATUS_TONE.DRAFT;
  const map: Record<Tone, { color: string; bg: string; line: string }> = {
    draft: { color: colors.ink3, bg: colors.paper2, line: colors.line },
    info: { color: colors.info, bg: colors.infoSoft, line: colors.infoLine },
    warn: { color: colors.warn, bg: colors.warnSoft, line: colors.warnLine },
    success: { color: colors.success, bg: colors.successSoft, line: colors.successLine },
    danger: { color: colors.danger, bg: colors.dangerSoft, line: colors.dangerLine },
    muted: { color: colors.ink4, bg: colors.paper2, line: colors.line },
  };
  const c = map[def.tone];
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, height: 22, paddingHorizontal: 9, borderRadius: 999, backgroundColor: c.bg, borderWidth: 1, borderColor: c.line, alignSelf: 'flex-start' }}>
      <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: c.color }} />
      <Text style={{ fontFamily: font.sans, fontSize: 11, fontWeight: '500', color: c.color }}>{def.label}</Text>
    </View>
  );
};

export default StatusPill;
